import React from 'react';

import { Box, Flex, Grid } from '@/components/ui';
import clsx from 'clsx';

export type SectionComponent = 'box' | 'flex' | 'grid';
export type SectionSize =
  | 'none'
  | 'default'
  | '3xsmall'
  | '2xsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | '2xlarge'
  | '3xlarge';
export type SectionColor =
  | 'muted'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'default';
export type SectionDisablePadding = 'none' | 'top' | 'bottom' | 'all';
export type SectionSpacingType = 'padding' | 'margin';

interface SectionProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * Base component for Section: 'box', 'flex', or 'grid'.
   * Default: 'box'
   */
  component?: SectionComponent;
  /**
   * Semantic padding size for the section.
   * Default: 'none'
   */
  size?: SectionSize;
  /**
   * Section color variant.
   * Default: 'default'
   */
  color?: SectionColor;
  /**
   * Disable padding for section: 'none', 'top', 'bottom', 'all'.
   * Default: 'none'
   */
  disablePadding?: SectionDisablePadding;
  /**
   * Controls whether the size prop affects padding (default) or margin.
   * 'padding' — size sets padding (default), 'margin' — size sets margin.
   */
  spacingType?: SectionSpacingType;
  /**
   * Render as a different element.
   */
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const componentMap = {
  box: Box,
  flex: Flex,
  grid: Grid,
};

const Section: React.FC<SectionProps> = ({
  children,
  className,
  component = 'box',
  size = 'none',
  color = 'default',
  disablePadding = 'none',
  spacingType = 'padding',
  asChild,
  ...rest
}) => {
  const Component = componentMap[component] || Box;
  const paddingClass =
    disablePadding === 'all'
      ? 'section--no-padding'
      : disablePadding === 'top'
        ? 'section--no-padding-top'
        : disablePadding === 'bottom'
          ? 'section--no-padding-bottom'
          : undefined;

  const componentProps: Record<string, unknown> = {
    className: clsx(
      'section',
      `section--size-${size}`,
      color && `section--color-${color}`,
      paddingClass,
      spacingType === 'margin' && 'section--spacing-margin',
      className
    ),
    ...rest,
  };
  if (asChild === true) componentProps['asChild'] = true;

  return <Component {...componentProps}>{children}</Component>;
};

export { Section };
